import React, { useEffect, useState } from 'react'
import { Row, Col, Table, Spinner, Button, Form, Container } from 'react-bootstrap'
import useFetch from '../../../_fetch'
import { ClickDataTable } from './click-table'


type Domain = {
    id: number
    domain: string
}

type Path = {
    id: number
    domain_id: number
    path: string
}

type Link = {
    id: number
    target_url: string
    hash: string
}

type Infos = {
    domains: Domain[]
    paths: Path[]
    links: Link[]
}

type ClicksPerLink = {
    link_id: number
    clicks: number
}

type ClicksPerPath = {
    path_id: number
    clicks: number
}

type ClicksPerCampaign = {
    campaign_id: number
    clicks: number
}

type IPublisher = {
  internalId: number
  id: string
  name: string
  url: string
  commission: number
  network: string
  country: string
  publisherId: string
  publisherName: string
  shopUrl: string
  credentialsId: string
}

const LinkScriptDashboard: React.FC = () => {
    const { doFetch } = useFetch()

    const [infos, setInfos] = useState<Infos>({ domains: [], paths: [], links: [] })
    const [clicksPerLink, setClicksPerLink] = useState<ClicksPerLink[]>([])
    const [clicksPerPath, setClicksPerPath] = useState<ClicksPerPath[]>([])
    const [clicksPerCampaign, setClicksPerCampaign] = useState<ClicksPerCampaign[]>([])
    const [publishers, setPublishers] = useState<IPublisher[]>([])
    
    const [loading, setLoading] = useState(true)

    const [fromDate, setFromDate] = useState(() => {
        const firstDay = new Date()
        firstDay.setDate(1)
        return firstDay.toISOString().substring(0, 10)
    })
    const [toDate, setToDate] = useState(() => new Date().toISOString().substring(0, 10))

    const fetchClicks = async (from?: string, to?: string) => {
        setLoading(true)
        try {
            const finalFrom = new Date(from || fromDate)
            finalFrom.setHours(0, 0, 0, 0)
            const finalTo = new Date(to || toDate)
            finalTo.setHours(23, 59, 59, 999)
            const resClicks = await doFetch('/byelink/aggregated-clicks', {
                fromDate: finalFrom.toISOString(),
                toDate: finalTo.toISOString()
            })
            if(resClicks.status >= 400) {
                throw await resClicks.text()
            }
            const clicksJson = await resClicks.json()
            setClicksPerLink(clicksJson.clicksPerLink)
            setClicksPerPath(clicksJson.clicksPerPath)
            setClicksPerCampaign(clicksJson.clicksPerCampaign)
        } catch (err) {
            console.error('Error fetching clicks:', err)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const resInfos = await doFetch('/byelink/infos')
                if(resInfos.status >= 400) {
                    throw await resInfos.text()
                }
                const infos = await resInfos.json()
                setInfos(infos)
                const res = await doFetch('/publisher-campaigns/all')
                if(res.status >= 400) {
                    throw await res.text()
                }
                const json = await res.json() as IPublisher[]
                setPublishers(json)

                await fetchClicks()
            } catch (err) {
                console.error('Error fetching initial data:', err)
                setLoading(false)
            }
        }
        fetchData()
    }, [])

    const domainClicks = infos.domains.map(domain => {
        const pathsOfDomain = infos.paths.filter(p => p.domain_id === domain.id)
        const totalClicks = pathsOfDomain.reduce((sum, p) => {
            const clicksObj = clicksPerPath.find(c => c.path_id === p.id)
            return sum + (clicksObj?.clicks || 0)
        }, 0)
        return { domain: domain.domain, clicks: totalClicks }
    })

    return (
        <Container fluid>
            <h3 className="mb-4">Overview</h3>

            <Form
                onSubmit={e => {
                    e.preventDefault()
                    fetchClicks(fromDate, toDate)
                }}
            >
                <Row className="align-items-end mb-3">
                    <Col xs={12} sm={4}>
                        <Form.Group controlId="fromDate">
                            <Form.Label>From date</Form.Label>
                            <Form.Control
                                type="date"
                                value={fromDate}
                                max={toDate}
                                required
                                onChange={e => setFromDate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} sm={4}>
                        <Form.Group controlId="toDate">
                            <Form.Label>To date</Form.Label>
                            <Form.Control
                                type="date"
                                value={toDate}
                                max={new Date().toISOString().substring(0, 10)}
                                required
                                onChange={e => setToDate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12} sm={4}>
                        <Button type="submit" className="mt-2" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : 'Submit'}
                        </Button>
                    </Col>
                </Row>
            </Form>

            <hr />

            {loading && clicksPerPath.length === 0 ? (
                <Row>
                    <Col className="text-center">
                        <Spinner animation="border" />
                    </Col>
                </Row>
            ) : (
                <>
                    <Row>
                        <Col md={6}>
                            <ClickDataTable
                                title="Domain"
                                data={domainClicks.map(d => ({ domain: d.domain, clicks: d.clicks }))}
                                columns={[
                                    { name: 'Domain', selector: row => row.domain, sortable: true, id: 'domain' },
                                    { name: 'Clicks', selector: row => row.clicks, sortable: true, id: 'clicks' }
                                ]}
                            />
                        </Col>

                        <Col md={6}>
                            <ClickDataTable
                                title="Path"
                                data={clicksPerPath.map(c => {
                                    const path = infos.paths.find(p => p.id === c.path_id)
                                    const domain = infos.domains.find(d => d.id === path?.domain_id)
                                    return { domain: domain?.domain, path: path?.path, clicks: c.clicks }
                                })}
                                columns={[
                                    { name: 'Domain', selector: row => row.domain||'Uknown', sortable: true, id: 'domain' },
                                    { name: 'Path', selector: row => row.path||'Uknown', sortable: true, id: 'path' },
                                    { name: 'Clicks', selector: row => row.clicks, sortable: true, id: 'clicks' }
                                ]}
                            />
                        </Col>

                        <Col md={6}>
                            <ClickDataTable
                                title="Link"
                                data={clicksPerLink.map(c => {
                                    const link = infos.links.find(l => l.id === c.link_id)
                                    return { url: link?.target_url, clicks: c.clicks }
                                })}
                                columns={[
                                    { name: 'Target URL', selector: row => row.url||'Uknown', sortable: true, id: 'targetUrl' },
                                    { name: 'Clicks', selector: row => row.clicks, sortable: true, id: 'clicks' }
                                ]}
                            />
                        </Col>

                        <Col md={6}>
                            <ClickDataTable
                                title="Campaign"
                                data={clicksPerCampaign.map(c => {
                                    const publisher = publishers.find(p => p.internalId === c.campaign_id)
                                    return {
                                        name: publisher?.name || 'Unknown',
                                        network: publisher?.network || 'Unknown',
                                        publisherName: publisher?.publisherName || 'Unknown',
                                        clicks: c.clicks
                                    }
                                })}
                                columns={[
                                    { name: 'Name', selector: row => row.name, sortable: true, id: 'name' },
                                    { name: 'Network', selector: row => row.network, sortable: true, id: 'network' },
                                    { name: 'Publisher', selector: row => row.publisherName, sortable: true, id: 'publisherName' },
                                    { name: 'Clicks', selector: row => row.clicks, sortable: true, id: 'clicks' }
                                ]}
                            />
                        </Col>

                    </Row>
                </>
            )}
        </Container>
    )
}

export default LinkScriptDashboard
